import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAfter, isBefore, addDays, isValid, isToday, isSameDay, isWithinInterval, getWeek, isEqual } from 'date-fns';
import { IconButton } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { ArrowBackSVGpath, ArrowForwardSVGpath } from '@skatteetaten/ds-icons';
import { Select } from '../Select/index.esm.js';
import { TextField } from '../TextField/index.esm.js';
import '../ErrorMessage/index.esm.js';
import '../LabelWithHelp/index.esm.js';
import '../Help/index.esm.js';
import '../SelectOption/index.esm.js';
import './styles.css';


const getDatePickerCalendarSelectedDateDefault = ()=>{
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const lastValidYear = 9999;
const sunday = 0;
function getDaysFromPrevMonth(datesInSelectedMonth) {
    const firstDayInSelectedMonth = datesInSelectedMonth[0].getDay();
    let daysFromPrevMonthInFirstWeekOfSelectedMonth;
    if (firstDayInSelectedMonth === sunday) {
        daysFromPrevMonthInFirstWeekOfSelectedMonth = 6;
    } else {
        daysFromPrevMonthInFirstWeekOfSelectedMonth = firstDayInSelectedMonth - 1;
    }
    return daysFromPrevMonthInFirstWeekOfSelectedMonth;
}
function getDaysFromNextMonth(datesInSelectedMonth) {
    const lastDayInSelectedMonth = datesInSelectedMonth[datesInSelectedMonth.length - 1].getDay();
    let daysFromNextMonthInLastWeekOfSelectedMonth;
    if (lastDayInSelectedMonth === sunday) {
        daysFromNextMonthInLastWeekOfSelectedMonth = 0;
    } else {
        daysFromNextMonthInLastWeekOfSelectedMonth = 7 - lastDayInSelectedMonth;
    }
    return daysFromNextMonthInLastWeekOfSelectedMonth;
}
function getLastDateInPrevMonth(year, monthIndex) {
    const lastDateInPrevMonth = new Date(year, monthIndex, 0);
    lastDateInPrevMonth.setFullYear(lastDateInPrevMonth.getFullYear());
    return lastDateInPrevMonth;
}
function getFirstDateInNextMonth(year, monthIndex) {
    const firstDateInNextMonth = new Date(year, monthIndex + 1, 1);
    firstDateInNextMonth.setFullYear(firstDateInNextMonth.getFullYear());
    return firstDateInNextMonth;
}
function getCalendarCells(year, monthIndex, minDate, maxDate, disabledDates) {
    const dates = getDatesInMonth(year, monthIndex);
    const cells = [];
    const daysFromPrevMonth = getDaysFromPrevMonth(dates);
    const lastDateInPrevMonth = getLastDateInPrevMonth(year, monthIndex);
    for(let i = 0; i < daysFromPrevMonth; i++){
        const date = new Date(lastDateInPrevMonth);
        const cell = {
            date,
            isAdjacentMonth: true,
            disabled: disabledDates?.some((d)=>isSameDay(d, date)) || !isWithinMinMaxRange(date, minDate, maxDate),
            isToday: isToday(date),
            text: String(date.getDate())
        };
        cells.unshift(cell);
        lastDateInPrevMonth.setDate(lastDateInPrevMonth.getDate() - 1);
    }
    dates.forEach((date)=>{
        const cell = {
            date,
            isAdjacentMonth: false,
            disabled: disabledDates?.some((d)=>isSameDay(d, date)) || !isWithinMinMaxRange(date, minDate, maxDate),
            isToday: isToday(date),
            text: String(date.getDate())
        };
        cells.push(cell);
    });
    const daysFromNextMonth = getDaysFromNextMonth(dates);
    const firstDateInNextMonth = getFirstDateInNextMonth(year, monthIndex);
    for(let i = 0; i < daysFromNextMonth; i++){
        const date = new Date(firstDateInNextMonth);
        const cell = {
            date,
            isAdjacentMonth: true,
            disabled: disabledDates?.some((d)=>isSameDay(d, date)) || !isWithinMinMaxRange(date, minDate, maxDate),
            isToday: isToday(date),
            text: String(date.getDate())
        };
        cells.push(cell);
        firstDateInNextMonth.setDate(firstDateInNextMonth.getDate() + 1);
    }
    return cells;
}
function getDatesInMonth(year, monthIndex) {
    const date = new Date(year, monthIndex);
    date.setFullYear(year);
    date.setDate(1);
    const month = date.getMonth();
    const dates = [];
    while(date.getMonth() === month){
        dates.push(new Date(date));
        date.setDate(date.getDate() + 1);
        date.setHours(0, 0, 0, 0);
    }
    return dates;
}
function getCalendarRows(year, monthIndex, minDate, maxDate, disabledDates) {
    year = findValidYear(year);
    const cells = getCalendarCells(year, monthIndex, minDate, maxDate, disabledDates);
    const rows = [];
    for(let i = 0; i < cells.length; i += 7){
        rows.push(cells.slice(i, i + 7));
    }
    return rows;
}
const isWithinMinMaxRange = (date, minDate, maxDate)=>{
    const firstValidDate = new Date('0001-01-01');
    const lastValidDate = new Date();
    lastValidDate.setMonth(11);
    lastValidDate.setDate(31);
    lastValidDate.setFullYear(lastValidYear);
    minDate?.setHours(0, 0, 0, 0);
    maxDate?.setHours(0, 0, 0, 0);
    const start = minDate && isValid(minDate) ? minDate : firstValidDate;
    const end = maxDate && isValid(maxDate) ? maxDate : lastValidDate;
    return isWithinInterval(date, {
        start,
        end
    });
};
const getNameOfMonthsAndDays = ()=>{
    return {
        monthNames: [
            dsI18n.t('ds_forms:datepicker.January'),
            dsI18n.t('ds_forms:datepicker.February'),
            dsI18n.t('ds_forms:datepicker.March'),
            dsI18n.t('ds_forms:datepicker.April'),
            dsI18n.t('ds_forms:datepicker.May'),
            dsI18n.t('ds_forms:datepicker.June'),
            dsI18n.t('ds_forms:datepicker.July'),
            dsI18n.t('ds_forms:datepicker.August'),
            dsI18n.t('ds_forms:datepicker.September'),
            dsI18n.t('ds_forms:datepicker.October'),
            dsI18n.t('ds_forms:datepicker.November'),
            dsI18n.t('ds_forms:datepicker.December')
        ],
        dayNames: [
            dsI18n.t('ds_forms:datepicker.Mon'),
            dsI18n.t('ds_forms:datepicker.Tue'),
            dsI18n.t('ds_forms:datepicker.Wed'),
            dsI18n.t('ds_forms:datepicker.Thu'),
            dsI18n.t('ds_forms:datepicker.Fri'),
            dsI18n.t('ds_forms:datepicker.Sat'),
            dsI18n.t('ds_forms:datepicker.Sun')
        ]
    };
};
function findValidYear(year) {
    return year === '' || year === 0 ? new Date().getFullYear() : Number(year);
}
const getFirstFocusableDate = (selectedDate, minDate, maxDate, disabledDatesTimestamps)=>{
    let focusableDate = selectedDate;
    focusableDate.setHours(0, 0, 0, 0);
    if (maxDate && isAfter(selectedDate, maxDate)) {
        focusableDate = maxDate;
        if (disabledDatesTimestamps?.has(focusableDate.getTime())) {
            focusableDate = findPreviousAvailableDate(focusableDate, disabledDatesTimestamps, minDate);
        }
        return focusableDate;
    } else if (minDate && isBefore(selectedDate, minDate)) {
        focusableDate = minDate;
        if (disabledDatesTimestamps?.has(focusableDate.getTime())) {
            focusableDate = findNextAvailableDate(focusableDate, disabledDatesTimestamps, minDate);
        }
        return focusableDate;
    }
    if (disabledDatesTimestamps?.has(focusableDate.getTime())) {
        focusableDate = findNextAvailableDate(focusableDate, disabledDatesTimestamps, maxDate);
    }
    return focusableDate;
};
const findNextAvailableDate = (startDate, disabledDatesTimestamps, maxDate)=>{
    if (!disabledDatesTimestamps || disabledDatesTimestamps.size === 0) {
        return addDays(startDate, 1);
    }
    startDate.setHours(0, 0, 0, 0);
    maxDate?.setHours(0, 0, 0, 0);
    const maxNextDate = maxDate && isValid(maxDate) ? maxDate : new Date().setFullYear(lastValidYear);
    let currentDate = addDays(startDate, 1);
    let currentTimestamp = currentDate.getTime();
    while(disabledDatesTimestamps.has(currentTimestamp) && isBefore(currentDate, maxNextDate)){
        currentDate = addDays(currentDate, 1);
        currentTimestamp = currentDate.getTime();
    }
    return currentDate;
};
const findPreviousAvailableDate = (startDate, disabledDatesTimestamps, minDate)=>{
    if (!disabledDatesTimestamps || disabledDatesTimestamps.size === 0) {
        return addDays(startDate, -1);
    }
    startDate.setHours(0, 0, 0, 0);
    minDate?.setHours(0, 0, 0, 0);
    const minPrevDate = minDate && isValid(minDate) ? minDate : new Date('0001-01-01');
    let currentDate = addDays(startDate, -1);
    let currentTimestamp = currentDate.getTime();
    while(disabledDatesTimestamps.has(currentTimestamp) && isBefore(minPrevDate, currentDate)){
        currentDate = addDays(currentDate, -1);
        currentTimestamp = currentDate.getTime();
    }
    return currentDate;
};

var styles = {
    "calendar": "DatePickerCalendar-module_calendar__cPFCv",
    "calendarNavigation": "DatePickerCalendar-module_calendarNavigation__ahX8U",
    "calendarNavigationMonthAndYearContainer": "DatePickerCalendar-module_calendarNavigationMonthAndYearContainer__eXcAb",
    "calendarNavigationArrowIcon": "DatePickerCalendar-module_calendarNavigationArrowIcon__DwL7X",
    "calendarNavigationMonth": "DatePickerCalendar-module_calendarNavigationMonth__-Yt8-",
    "calendarNavigationYear": "DatePickerCalendar-module_calendarNavigationYear__xRWWh",
    "calendarTable": "DatePickerCalendar-module_calendarTable__MZx-C",
    "calendarTableHeaderCell": "DatePickerCalendar-module_calendarTableHeaderCell__OIu6R",
    "calendarTableDateButton": "DatePickerCalendar-module_calendarTableDateButton__kzq9R",
    "calendarTableDateButton_adjacentMonth": "DatePickerCalendar-module_calendarTableDateButton_adjacentMonth__Tlod2",
    "calendarTableDateButton_today": "DatePickerCalendar-module_calendarTableDateButton_today__MWBYs",
    "srOnly": "DatePickerCalendar-module_srOnly__kMLrx"
};

const DatePickerCalendar = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, disabledDates, minDate, maxDate, selectedDate = getDatePickerCalendarSelectedDateDefault(), onSelectDate, onTabKeyOut }, ref)=>{
    const { t } = useTranslation('ds_forms', {
        i18n: dsI18n
    });
    const calendarRef = useRef(null);
    const disabledDateTimestamps = useMemo(()=>new Set(disabledDates?.map((date)=>{
            date.setHours(0, 0, 0, 0);
            return date.getTime();
        })), [
        disabledDates
    ]);
    const firstFocusableDate = getFirstFocusableDate(selectedDate, minDate, maxDate, disabledDateTimestamps);
    const [selectedMonthIndex, setSelectedMonthIndex] = useState(firstFocusableDate.getMonth());
    const [selectedYear, setSelectedYear] = useState(firstFocusableDate.getFullYear());
    const [isPrevMonthInvalid, setIsPrevMonthInvalid] = useState(false);
    const [isNextMonthInvalid, setIsNextMonthInvalid] = useState(false);
    const [firstValidYear, lastValidYear] = [
        1,
        9999
    ];
    const [january, december] = [
        0,
        11
    ];
    const { monthNames, dayNames } = getNameOfMonthsAndDays();
    const isFirstFocusableDateInView = firstFocusableDate.getFullYear() === selectedYear && firstFocusableDate.getMonth() === selectedMonthIndex;
    const cellBtnThatWillReceiveFocus = isFirstFocusableDateInView ? firstFocusableDate : getFirstFocusableDate(new Date(Number(selectedYear), selectedMonthIndex, 1), minDate, maxDate, disabledDateTimestamps);
    const isMonthInvalid = (monthIndex, year, isPrevMonth)=>{
        return isPrevMonth ? monthIndex === january && year === firstValidYear : monthIndex === december && year === lastValidYear;
    };
    const onPrevMonth = ()=>{
        let prevMonthIndex = selectedMonthIndex - 1;
        const year = findValidYear(selectedYear);
        setIsPrevMonthInvalid(isMonthInvalid(prevMonthIndex, year, true));
        setIsNextMonthInvalid(false);
        const isPrevMonthNewYear = prevMonthIndex === -1;
        if (isPrevMonthNewYear) {
            prevMonthIndex = december;
            setSelectedYear(year - 1);
        }
        setSelectedMonthIndex(prevMonthIndex);
    };
    const onNextMonth = ()=>{
        let nextMonthIndex = selectedMonthIndex + 1;
        const year = findValidYear(selectedYear);
        setIsNextMonthInvalid(isMonthInvalid(nextMonthIndex, year, false));
        setIsPrevMonthInvalid(false);
        const isNextMonthNewYear = nextMonthIndex === 12;
        if (isNextMonthNewYear) {
            nextMonthIndex = january;
            setSelectedYear(year + 1);
        }
        setSelectedMonthIndex(nextMonthIndex);
    };
    const handleMonthChange = (e)=>{
        const monthIndex = Number(e.target.value);
        const year = findValidYear(selectedYear);
        setSelectedMonthIndex(monthIndex);
        setIsPrevMonthInvalid(isMonthInvalid(monthIndex, year, true));
        setIsNextMonthInvalid(isMonthInvalid(monthIndex, year, false));
    };
    const handleYearChange = (e)=>{
        const year = Number(e.target.value);
        const isNotValid = isNaN(year) || year < firstValidYear || year > lastValidYear;
        setSelectedYear(isNotValid ? '' : year);
        setIsPrevMonthInvalid(isMonthInvalid(selectedMonthIndex, year, true));
        setIsNextMonthInvalid(isMonthInvalid(selectedMonthIndex, year, false));
    };
    const handleYearBlur = (e)=>{
        const input = e.target;
        if (input.value === '') {
            setSelectedYear(new Date().getFullYear());
            /* onBlur med mutating state gjør at onClick til dato-knappene ikke utløses derfor gjøres dette */ const target = e.relatedTarget ? e.relatedTarget : null;
            target?.click();
        }
    };
    const updateFocus = (currentDate, dateToFocus)=>{
        if (!isWithinMinMaxRange(dateToFocus, minDate, maxDate)) {
            return;
        }
        const isDifferentMonth = dateToFocus.getMonth() !== currentDate.getMonth();
        const isNewYear = dateToFocus.getFullYear() !== currentDate.getFullYear();
        if (isDifferentMonth && !isMonthInvalid(dateToFocus.getMonth(), dateToFocus.getFullYear(), dateToFocus.getMonth() < currentDate.getMonth())) {
            setSelectedMonthIndex(dateToFocus.getMonth());
            if (isNewYear) {
                setSelectedYear(dateToFocus.getFullYear());
            }
        }
        setTimeout(()=>{
            if (!calendarRef.current) {
                return;
            }
            const buttonToFocus = calendarRef.current.querySelector(`#btn-${dateToFocus.getFullYear()}-${dateToFocus.getMonth()}-${dateToFocus.getDate()}`);
            buttonToFocus?.focus();
        }, 0);
    };
    const handleKeyboardNavigation = (event, currentDate)=>{
        switch(event.key){
            case 'ArrowUp':
                {
                    event.preventDefault();
                    const newFocusableDate = findPreviousAvailableDate(addDays(currentDate, -6), disabledDateTimestamps, minDate);
                    updateFocus(currentDate, newFocusableDate);
                    break;
                }
            case 'ArrowDown':
                {
                    event.preventDefault();
                    const newFocusableDate = findNextAvailableDate(addDays(currentDate, 6), disabledDateTimestamps, maxDate);
                    updateFocus(currentDate, newFocusableDate);
                    break;
                }
            case 'ArrowLeft':
                {
                    event.preventDefault();
                    const newFocusableDate = findPreviousAvailableDate(currentDate, disabledDateTimestamps, minDate);
                    updateFocus(currentDate, newFocusableDate);
                    break;
                }
            case 'ArrowRight':
                {
                    event.preventDefault();
                    const newFocusableDate = findNextAvailableDate(currentDate, disabledDateTimestamps, maxDate);
                    updateFocus(currentDate, newFocusableDate);
                    break;
                }
            case 'Tab':
                {
                    if (!event.shiftKey) {
                        event.preventDefault();
                        onTabKeyOut && onTabKeyOut();
                    }
                    break;
                }
            default:
                return;
        }
    };
    const grid = useMemo(()=>getCalendarRows(selectedYear, selectedMonthIndex, minDate, maxDate, disabledDates), [
        selectedYear,
        selectedMonthIndex,
        minDate,
        maxDate,
        disabledDates
    ]);
    const concatenatedClassName = `${styles.calendar} ${className}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        ref: ref,
        id: id,
        className: concatenatedClassName,
        lang: lang,
        "data-testid": dataTestId,
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: styles.calendarNavigation,
                children: [
                    /*#__PURE__*/ jsx(IconButton, {
                        className: styles.calendarNavigationArrowIcon,
                        svgPath: ArrowBackSVGpath,
                        title: `${t('datepicker.PreviousMonth')} ${monthNames[selectedMonthIndex === 0 ? 11 : selectedMonthIndex - 1]} ${selectedMonthIndex === 0 ? Number(selectedYear) - 1 : selectedYear}`,
                        type: 'button',
                        disabled: isPrevMonthInvalid,
                        onClick: ()=>onPrevMonth()
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.calendarNavigationMonthAndYearContainer,
                        children: [
                            /*#__PURE__*/ jsx(Select, {
                                classNames: {
                                    selectContainer: styles.calendarNavigationMonth
                                },
                                value: selectedMonthIndex,
                                label: t('datepicker.SelectMonth'),
                                hidePlaceholder: true,
                                hideLabel: true,
                                onChange: handleMonthChange,
                                children: monthNames.map((month, index)=>{
                                    return /*#__PURE__*/ jsx(Select.Option, {
                                        value: index,
                                        children: month
                                    }, month);
                                })
                            }),
                            /*#__PURE__*/ jsx(TextField, {
                                className: styles.calendarNavigationYear,
                                value: selectedYear,
                                label: t('datepicker.Year'),
                                inputMode: 'numeric',
                                hideLabel: true,
                                onChange: handleYearChange,
                                onBlur: handleYearBlur
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(IconButton, {
                        className: styles.calendarNavigationArrowIcon,
                        svgPath: ArrowForwardSVGpath,
                        title: `${t('datepicker.NextMonth')} ${monthNames[selectedMonthIndex === 11 ? 0 : selectedMonthIndex + 1]} ${selectedMonthIndex === 11 ? Number(selectedYear) + 1 : selectedYear}`,
                        type: 'button',
                        disabled: isNextMonthInvalid,
                        onClick: ()=>onNextMonth()
                    })
                ]
            }),
            /*#__PURE__*/ jsxs("table", {
                ref: calendarRef,
                className: styles.calendarTable,
                children: [
                    /*#__PURE__*/ jsx("caption", {
                        className: styles.srOnly,
                        children: `${monthNames[selectedMonthIndex]} ${selectedYear}`
                    }),
                    /*#__PURE__*/ jsx("thead", {
                        children: /*#__PURE__*/ jsx("tr", {
                            children: dayNames.map((day)=>{
                                return /*#__PURE__*/ jsx("th", {
                                    className: styles.calendarTableHeaderCell,
                                    children: day
                                }, day);
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx("tbody", {
                        children: grid.map((cells, rowIdx)=>{
                            const weekIdx = getWeek(grid[rowIdx][0].date);
                            return /*#__PURE__*/ jsx("tr", {
                                children: cells.map((cell)=>{
                                    const adjancentMonthClassName = cell.isAdjacentMonth ? styles.calendarTableDateButton_adjacentMonth : '';
                                    const todayClassName = cell.isToday ? styles.calendarTableDateButton_today : '';
                                    const buttonClassName = `${styles.calendarTableDateButton} ${adjancentMonthClassName} ${todayClassName}`.trim();
                                    const ariaLabel = `${cell.isToday ? t('datepicker.Today') : ''} ${cell.date.getDate()}. ${monthNames[cell.date.getMonth()]} ${cell.date.getFullYear()}`;
                                    const ariaCurrent = isEqual(cell.date, firstFocusableDate.setHours(0, 0, 0, 0)) ? 'true' : undefined;
                                    return /*#__PURE__*/ jsx("td", {
                                        children: /*#__PURE__*/ jsx("button", {
                                            id: `btn-${cell.date.getFullYear()}-${cell.date.getMonth()}-${cell.date.getDate()}`,
                                            className: buttonClassName,
                                            type: 'button',
                                            disabled: cell.disabled,
                                            tabIndex: isEqual(cell.date, cellBtnThatWillReceiveFocus) ? 0 : -1,
                                            "aria-current": ariaCurrent,
                                            "aria-label": ariaLabel,
                                            onClick: ()=>{
                                                onSelectDate(cell.date);
                                            },
                                            onKeyDown: (event)=>{
                                                handleKeyboardNavigation(event, cell.date);
                                            },
                                            children: `${cell.text}`
                                        })
                                    }, `cell-${cell.date.toLocaleDateString()}`);
                                })
                            }, `row-${selectedYear}-${selectedMonthIndex}-${weekIdx}`);
                        })
                    })
                ]
            })
        ]
    });
});
DatePickerCalendar.displayName = 'DatePickerCalender';

export { DatePickerCalendar };
